import React, { ChangeEvent, useState, useEffect } from "react";
import styled from "styled-components";
import { useGlobalModal } from "../../../contexts/GlobalModalContext";
import { PrimaryButtonStyled } from "../PrimaryButtonStyled";
import { OutlinedButtonStyled } from "../OutlinedButtonStyled";
import { buildOrgHeaderLabel } from "../../../util/ClubDisplayUtil";

const ModalBody = styled.div.attrs(() => ({
    className: "m-4",
}))`
    height: 230px;
`;

const PersonnelInfoSection = styled.div.attrs(() => ({
    className: "mb-3",
}))``;

const PersonnelNameStyle = styled.div.attrs(() => ({ className: "mb-1" }))`
    font-family: Helvetica;
    font-size: 16px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    color: var(--slate);
`;

const AffiliateInfoStyle = styled.div.attrs(() => ({}))`
    font-family: Helvetica;
    font-size: 12px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    color: var(--slate);
`;

const ExemptCommentTextArea = styled.textarea.attrs(() => ({}))`
    width: 100%;
    resize: none;
    height: 100px;
    border-radius: 4px;
    border: solid 1px var(--fog);
    background-color: var(--white);
    font-family: Helvetica;
    font-size: 14px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    color: var(--black);
    padding: 10px;
`;

type ExemptPersonnelBodyProps = {
    personnelInfo: PersonnelInfoType;
    exemptComment: string;
    setExemptComment: Function;
};

type ExemptPersonnelFooterProps = {
    personnelInfo: PersonnelInfoType;
    exemptComment: string;
    setExemptComment: Function;
    exemptPersonnelCallback: (
        rosterId: number,
        exemptComment: string,
        setExemptComment: Function,
    ) => void | Promise<void>;
};

type PersonnelInfoType = {
    id: number;
    lastName: string;
    firstName: string;
    title: string;
    affiliation: {
        orgAbbreviation: string;
        name: string;
    };
    roster: {
        id: number;
    };
};

const ExemptPersonnelBody: React.FC<ExemptPersonnelBodyProps> = ({
    personnelInfo,
    exemptComment,
    setExemptComment,
}) => {
    const commentChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setExemptComment(event.currentTarget.value);
    };

    return (
        <ModalBody>
            <PersonnelInfoSection>
                <PersonnelNameStyle>
                    {personnelInfo.lastName}, {personnelInfo.firstName} - {personnelInfo.title}
                </PersonnelNameStyle>
                <AffiliateInfoStyle>{buildOrgHeaderLabel(personnelInfo)}</AffiliateInfoStyle>
            </PersonnelInfoSection>
            <div>
                <label className="required">Comments</label>
                <div>
                    <ExemptCommentTextArea value={exemptComment} onChange={commentChangeHandler} />
                </div>
            </div>
        </ModalBody>
    );
};

const ExemptPersonnelFooter: React.FC<ExemptPersonnelFooterProps> = ({
    personnelInfo,
    exemptComment,
    setExemptComment,
    exemptPersonnelCallback,
}) => {
    const { closeModal } = useGlobalModal();

    const [markExemptDisabled, setMarkExemptDisabled] = useState<boolean>(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setMarkExemptDisabled(!exemptComment?.trim());
    }, [exemptComment]);

    return (
        <div className="d-flex">
            <div className="mr-2">
                <PrimaryButtonStyled
                    width={127}
                    height={26}
                    disabled={markExemptDisabled || submitting}
                    onClick={async event => {
                        event.stopPropagation();
                        setSubmitting(true);
                        try {
                            await exemptPersonnelCallback(
                                personnelInfo.roster.id,
                                exemptComment,
                                setExemptComment,
                            );
                            closeModal();
                        } catch {
                            // Keep modal open for retry after error toast.
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    Mark Exempt
                </PrimaryButtonStyled>
            </div>

            <OutlinedButtonStyled
                width={80}
                height={26}
                onClick={event => {
                    event.stopPropagation();
                    setExemptComment("");
                    closeModal();
                }}
            >
                Cancel
            </OutlinedButtonStyled>
        </div>
    );
};

ExemptPersonnelBody.displayName = "BodyContent";
ExemptPersonnelFooter.displayName = "FooterContent";

export { ExemptPersonnelBody, ExemptPersonnelFooter, PersonnelInfoType };
